import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Container, Carousel, Row, Column, List, Icon } from '@pella/sash-framework';
import { Image, CTA } from '../../../';
import { isValidArray } from '../../../utilities/validations';

import ResultsTile from '../../../../templates/plp/results/tile';
import CardRenderer from '../../../common/card-types/card-renderer';

import styles from './index.module.scss';

const ShopByGroup = ({ data, className, ...other }) => {
	const [activeTab, setActiveTab] = useState(0);

	if (!data) return null;

	const onTabClick = (index) => setActiveTab(index);

	return (
		<div
			id={data.anchor_text}
			className={className || ''}
			{...other}
		>
			{data.heading && (
				<div className='bg-black py-2'>
					<Container>
						<h2 className='m-0 text-lg font-normal text-white md:text-3xl md:!leading-tight'>{data.heading}</h2>
					</Container>
				</div>
			)}

			{/* Tab Slider */}
			{isValidArray(data?.product) && (
				<div className='bg-white py-2 md:py-6'>
					<Container>
						<Carousel
							className={styles.shopBySlider}
							arrowsClassName={styles.shopByArrows}
							slidePadding={24}
							slidesToShow={data.shop_by === 'type' ? { xs: 2.5, md: 4, lg: 6, xl: 7 } : data.shop_by === 'product-line' ? { xs: 2, md: 3, lg: 4, xl: 5 } : { xs: 3, md: 6, xl: 8 }}
							showArrows
						>
							{data.product.map((tab, index) => (
								<button
									onClick={() => onTabClick(index)}
									key={index}
									className={`${activeTab === index ? styles.activeTabBtn : styles.tabBtn} flex h-full w-full flex-col items-center pb-4 max-md:justify-center md:px-6 md:pb-6`}
								>
									{tab.image?.[0] && (
										<Image
											data={tab.image[0]}
											className={`mb-3 block`}
											objectFit='contain'
										/>
									)}
									{tab.heading && <span className={`tab-heading ${activeTab === index ? 'font-semibold text-black' : 'text-neutral-600'} ${data.shop_by === 'product-line' ? 'mb-1 text-lg md:mb-2' : ''} !leading-tight`}>{tab.heading}</span>}
									{tab.material && <span className={`md:mb-auto ${activeTab === index ? 'text-neutral-900' : 'text-neutral-600'} block`}>{tab.material}</span>}
									{tab.flag_type && (
										<span className={`${styles.featureBadge} mt-2 flex items-center rounded-full bg-neutral-25 px-2 py-1 text-sm font-semibold md:mt-3 xl:mt-4`}>
											<Icon
												name='auto_awesome'
												className='mr-2'
											/>
											{tab.flag_type}
										</span>
									)}
								</button>
							))}

							{data.group?.body && (
								<CardRenderer
									data={{
										card_type: 'icon',
										description: data.group.body,
										cta: [{ link: data.group.link }],
									}}
									className={styles.cardTab}
								/>
							)}
						</Carousel>
					</Container>
				</div>
			)}

			{/* Tab Content */}
			{isValidArray(data?.product) && data.product[activeTab]?.group && (
				<div className='bg-neutral-50 py-6 md:py-8'>
					<Container>
						<Row largeGutters>
							{isValidArray(data.product[activeTab].group.image) && (
								<Column
									xs={12}
									md={6}
									xl={3}
								>
									<Image data={data.product[activeTab].group.image[0]} />
								</Column>
							)}

							<Column
								xs={12}
								xl='fluid'
							>
								{data.product[activeTab].group.heading && <h3 className='m-0 font-semibold'>{data.product[activeTab].group.heading}</h3>}
								{data.product[activeTab].group.description && (
									<div
										className='mt-4 text-sm md:mt-6 md:text-lg'
										dangerouslySetInnerHTML={{ __html: data.product[activeTab].group.description }}
									/>
								)}
								{data.product[activeTab].flag_type && (
									<p className='mt-2 md:mt-3 xl:mt-4'>
										<span className={`${styles.featureBadge} inline-flex items-center rounded-full bg-neutral-25 px-2 py-1 text-sm font-semibold`}>
											<Icon
												name='auto_awesome'
												className='mr-2'
											/>
											{data.product[activeTab].flag_type}
										</span>
									</p>
								)}
								{isValidArray(data.product[activeTab].group.cta) && (
									<List
										bullets='none'
										className={`${styles.ctaList} flex-md md:flex-wrap xl:inline-block`}
									>
										{data.product[activeTab].group.cta.map((cta, index) => (
											<li
												key={index}
												className='mt-2 md:mr-2 xl:mr-0'
											>
												<CTA
													data={cta}
													className='w-full'
												/>
											</li>
										))}
									</List>
								)}
							</Column>

							{isValidArray(data.product[activeTab].group.tiles) && (
								<Column
									xs={12}
									xl={7}
								>
									<Row className='mt-6 md:mt-8 xl:mt-0'>
										{data.product[activeTab].group.tiles.map((tile, index) => (
											<Column
												key={index}
												xs={12}
												md='fluid'
												className={index > 0 ? 'mt-4 md:mt-0' : null}
											>
												<ResultsTile
													tile={tile}
													pdpURL={tile.static_url || null}
													key={tile.backend_name}
												/>
											</Column>
										))}
									</Row>
								</Column>
							)}
						</Row>
					</Container>
				</div>
			)}
		</div>
	);
};

ShopByGroup.propTypes = {
	data: PropTypes.object,
	className: PropTypes.string,
};

export default ShopByGroup;
